import {
  collection,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from "firebase/firestore";
import { getFirebaseApp } from "./firebaseClient";
import type { CourseChatMessage } from "services/courseChatService";
import type { DirectChatBootstrapResponse } from "services/directChatService";

type FirestoreDmMessageDoc = {
  id: string;
  conversationId: number;
  senderId: number;
  senderRole: string;
  senderName: string | null;
  senderAvatar: string | null;
  type: CourseChatMessage["message_type"];
  text: string | null;
  attachmentPath: string | null;
  attachmentName: string | null;
  attachmentMime: string | null;
  attachmentSize: number | null;
  voiceDurationSeconds: number | null;
  clientMessageId: string | null;
  createdAtMillis: number;
  isDeleted: boolean;
};

function mapFirestoreDmMessage(docData: FirestoreDmMessageDoc, viewerAppUserId: number): CourseChatMessage {
  const isDeleted = Boolean(docData.isDeleted);
  const attachment: CourseChatMessage["attachment"] =
    !isDeleted && docData.attachmentPath
      ? {
          url: null as string | null,
          path: docData.attachmentPath,
          name: docData.attachmentName,
          mime: docData.attachmentMime,
          size: docData.attachmentSize,
          duration_seconds: docData.voiceDurationSeconds,
        }
      : null;

  return {
    id: docData.id,
    course_id: docData.conversationId,
    sender: {
      id: docData.senderId,
      name: docData.senderName,
      role: docData.senderRole,
      avatar_url: docData.senderAvatar,
    },
    message_type: docData.type,
    body: isDeleted ? null : docData.text,
    attachment,
    client_message_id: docData.clientMessageId ?? null,
    is_mine: docData.senderId === viewerAppUserId,
    read_count: null,
    created_at: docData.createdAtMillis ? new Date(docData.createdAtMillis).toISOString() : null,
    created_at_millis: docData.createdAtMillis,
    created_at_human: null,
    is_deleted: isDeleted,
  };
}

export function listenToDirectChat(
  bootstrap: Pick<DirectChatBootstrapResponse, "firebase">,
  viewerAppUserId: number,
  handlers: {
    onMessages: (messages: CourseChatMessage[]) => void;
    onError?: (error: Error) => void;
  },
  messageLimit = 100
): Unsubscribe {
  const db = getFirestore(getFirebaseApp());
  const messagesRef = collection(db, bootstrap.firebase.messages_path);
  const messagesQuery = query(messagesRef, orderBy("createdAtMillis", "asc"), limit(messageLimit));

  return onSnapshot(
    messagesQuery,
    (snapshot) => {
      const messages = snapshot.docs.map((item) =>
        mapFirestoreDmMessage(item.data() as FirestoreDmMessageDoc, viewerAppUserId)
      );
      handlers.onMessages(messages);
    },
    (error) => {
      handlers.onError?.(error);
    }
  );
}
